import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import SiteLayout from '../components/SiteLayout';
import GumballIcon from '../components/GumballIcon';
import { publicApi } from '../api/client';
import {
  MEDIA_FILTERS,
  formatMediaDate,
  buildFeedOrder,
  mediaDetailPath,
} from './media-shared';
import './media-detail.css';

function FeedPost({ item, isActive, postRef, category }) {
  const [copied, setCopied] = useState(false);
  const postUrl = `${window.location.origin}${mediaDetailPath(item, category)}`;

  const handleShare = async () => {
    const payload = {
      title: item.title,
      text: item.description || item.title,
      url: postUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(payload);
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <article
      ref={postRef}
      id={`media-post-${item.id}`}
      className={`media-feed-post glass-card ${isActive ? 'media-feed-post--active' : ''}`}
    >
      <div className="media-feed-media-wrap">
        {item.file_type === 'video' ? (
          <video
            src={item.file_url}
            controls
            playsInline
            preload="metadata"
            className="media-feed-media"
          />
        ) : (
          <img src={item.file_url} alt={item.title} className="media-feed-media" loading="lazy" />
        )}
      </div>

      <div className="media-feed-body">
        <div className="media-feed-meta">
          <span className={`category-tag category-${item.category}`}>{item.category}</span>
          <time dateTime={item.created_at}>{formatMediaDate(item.created_at)}</time>
        </div>
        <h2 className="media-feed-title">{item.title}</h2>
        {item.description && <p className="media-feed-caption">{item.description}</p>}
        <div className="media-feed-actions">
          <button type="button" className="media-feed-action-btn" onClick={handleShare}>
            <GumballIcon name="external" size={16} /> Share
          </button>
          <button type="button" className="media-feed-action-btn" onClick={handleCopy}>
            <GumballIcon name="link" size={16} /> {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function MediaDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const category = searchParams.get('category') || 'all';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const activeRef = React.useRef(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    const query = category === 'all' ? undefined : category;
    publicApi.getMedia(query)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setItems(list);
        const exists = list.some((i) => String(i.id) === String(id));
        if (!exists && list.length) setNotFound(true);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [id, category]);

  const feed = buildFeedOrder(items, id);
  const navList = items;
  const currentIdx = navList.findIndex((i) => String(i.id) === String(id));
  const currentItem = currentIdx >= 0 ? navList[currentIdx] : null;
  const prevItem = currentIdx > 0 ? navList[currentIdx - 1] : null;
  const nextItem = currentIdx >= 0 && currentIdx < navList.length - 1 ? navList[currentIdx + 1] : null;

  useEffect(() => {
    if (!loading && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [id, loading]);

  const goTo = useCallback((item) => {
    navigate(mediaDetailPath(item, category));
  }, [navigate, category]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' && prevItem) goTo(prevItem);
      if (e.key === 'ArrowRight' && nextItem) goTo(nextItem);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [prevItem, nextItem, goTo]);

  const backHref = category !== 'all' ? `/media?category=${encodeURIComponent(category)}` : '/media';

  return (
    <SiteLayout pageClass="media-detail-page">
      <div className="media-detail-decor" aria-hidden="true">
        <span className="media-bubble media-bubble-1" />
        <span className="media-bubble media-bubble-2" />
        <span className="media-bubble media-bubble-3" />
      </div>

      <div className="media-detail-wrap">
        <header className="media-detail-toolbar">
          <Link to={backHref} className="media-detail-back">
            <GumballIcon name="photo" size={18} /> Back to Media
          </Link>
          <div className="media-detail-nav media-detail-nav--desktop">
            <button
              type="button"
              className="media-detail-nav-btn"
              disabled={!prevItem}
              onClick={() => prevItem && goTo(prevItem)}
              aria-label="Previous post"
            >
              ← Prev
            </button>
            <button
              type="button"
              className="media-detail-nav-btn"
              disabled={!nextItem}
              onClick={() => nextItem && goTo(nextItem)}
              aria-label="Next post"
            >
              Next →
            </button>
          </div>
        </header>

        {category !== 'all' && (
          <p className="media-detail-filter-note">
            Showing <strong>{MEDIA_FILTERS.find((f) => f.key === category)?.label || category}</strong> posts
          </p>
        )}

        {loading && <p className="public-loading">Loading...</p>}

        {notFound && (
          <div className="public-empty glass-card">
            <h2>Post not found</h2>
            <p>This media item may be unpublished or removed.</p>
            <Link to="/media" className="cta-btn cta-secondary">Back to Media</Link>
          </div>
        )}

        {!loading && !notFound && currentItem && (
          <>
            <div className="media-feed media-feed--single" aria-live="polite">
              <FeedPost
                key={currentItem.id}
                item={currentItem}
                isActive
                category={category}
              />
            </div>

            <div className="media-feed media-feed--scroll">
              {feed.map((item) => (
                <FeedPost
                  key={item.id}
                  item={item}
                  isActive={String(item.id) === String(id)}
                  postRef={String(item.id) === String(id) ? activeRef : null}
                  category={category}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </SiteLayout>
  );
}
